import { trpc } from '@tencent/tpro_rmonitor_product_manager_approval';
import { message, Space, Table, Divider, Modal, ModalProps, Form, Input, Select, Popconfirm } from 'antd';
import { ColumnsType } from 'antd/es/table';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useRole } from '@/presentation/hooks';
import { approvalService } from '@/service/approval';
import { WithTypeCast } from '@raft/utils';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
type IApplication = WithTypeCast<trpc.rmonitor.product_manager.IApplication>;

const { Expire } = trpc.rmonitor.product_manager;

export enum ApprovalStatus {
  Pending = 'pending',
  Rejected = 'rejected',
  Approved = 'approved'
}

const approvalStatus: Record<ApprovalStatus, {text: string, color: string}> = {
  [ApprovalStatus.Pending]: {
    text: i18n.t('待审批'),
    color: 'processing',
  },
  [ApprovalStatus.Rejected]: {
    text: i18n.t('已驳回'),
    color: '#DB3543',
  },
  [ApprovalStatus.Approved]: {
    text: i18n.t('已同意'),
    color: '#00C788',
  },
};

type ApprovalModalProp = ModalProps & {
  initValues?: IApplication;
  onSuccess?: () => void;
};

export const EXPIRE_OPTIONS = [
  { label: i18n.t('三个月'), value: Expire.THREE_MONTHS },
  { label: i18n.t('半年'), value: Expire.HALF_YEAR },
  { label: i18n.t('一年'), value: Expire.ONE_YEAR },
  { label: i18n.t('两年'), value: Expire.TWO_YEARS },
  { label: i18n.t('永久'), value: Expire.FOREVER },
];

export const getExpireTime = (date: trpc.rmonitor.product_manager.Expire): number | null => {
  switch (date) {
    case Expire.ONE_YEAR:
      return moment().add(1, 'year')
        .unix();
    case Expire.THREE_MONTHS:
      return moment().add(3, 'months')
        .unix();
    case Expire.TWO_YEARS:
      return moment().add(2, 'years')
        .unix();
    case Expire.HALF_YEAR:
      return moment().add(6, 'months')
        .unix();
    default:
      return null;
  }
};

const ApprovalModal = (props: ApprovalModalProp)  => {
  const { t } = useTranslation();
  const { initValues, ...restProps } = props;
  const [form] = Form.useForm();
  const { rolesOption } = useRole();

  // 同意
  const doApprove = async () => {
    const record = form.getFieldsValue();
    const res = await approvalService.approve({
      applicationId: initValues?.id ?? 0,
      roleId: record?.roleId ?? 0,
      expireTime: getExpireTime(record?.expireType),
      expireType: record?.expireType,
    });
    if (res) {
      message.success(t("审批成功"));
      props.onSuccess?.();
    }
  };

  useEffect(() => {
    form.setFieldsValue(props.initValues);
  }, [props.initValues]);

  return (
    <Modal
      visible={props.visible}
      destroyOnClose
      width={528}
      title={t("审批权限")}
      onOk={doApprove}
      {...restProps}
    >
      <Form form={form} labelCol={{ span: 4 }} labelAlign={'left'} initialValues={{
        roleId: initValues?.roleId,
        expireType: initValues?.expireType,
        user: initValues?.userName,
      }}>
        <Form.Item label={t("申请人")} name={'user'}>
          <Input disabled />
        </Form.Item>
        <Form.Item label={t("角色")} name={'roleId'}>
          <Select options={rolesOption} />
        </Form.Item>
        <Form.Item label={t("有效期")} name={'expireType'}>
          <Select options={EXPIRE_OPTIONS} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

interface IInternalApproval {
  loading: boolean;
  dataSource: IApplication[];
  load: () => void
}
export const InternalApproval = (props: IInternalApproval) => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [record, setRecord] = useState<IApplication | undefined>(undefined);
  const { loading, dataSource, load } = props;

  const columns: ColumnsType<IApplication> = [
    {
      key: 'userName',
      title: t("申请人"),
      dataIndex: 'userName',
      align: 'left',
      width: '20%',
      render: (_text: unknown, record: IApplication) => `${record.userName}（${record.buglyId}）`,
    },
    {
      key: 'applyTime',
      title: t("申请时间"),
      dataIndex: 'applyTime',
      align: 'left',
      width: '15%',
      render: (time: number) => (time ? moment(time * 1000).format('YYYY-MM-DD HH:mm:ss') : '-'),
    },
    {
      key: 'roleName',
      title: t("角色"),
      dataIndex: 'roleName',
      align: 'left',
      width: '15%',
    },
    {
      key: 'status',
      title: t("状态"),
      dataIndex: 'status',
      align: 'left',
      width: '15%',
      render: (status: ApprovalStatus, record) => {
        const currentStatus = approvalStatus[status];
        if (status === ApprovalStatus.Pending) {
          return (
            <Space size={0} split={<Divider type="vertical" />}>
              <span style={{ color: '#0A6CFF', cursor: 'pointer' }} onClick={() => {
                setRecord(record);
                setVisible(true);
              }}>{t("审批")}</span>
              <Popconfirm title={t("确认驳回？")} onConfirm={() => {
                doReject(record);
              }}>
                <span style={{ color: '#DB3543', cursor: 'pointer' }}>{t("驳回")}</span>
              </Popconfirm>
            </Space>
          );
        }
        return <span style={{ color: currentStatus.color }}>{currentStatus.text}</span>;
      },
    },
  ];


  const doReject = async (record: IApplication) => {
    const res = await approvalService.reject({
      applicationId: [record.id as number],
    });

    if (res) {
      message.success(t("驳回成功"));
      load();
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div style={{ padding: '10px 0' }}>
      <Table<IApplication>
        loading={loading}
        columns={columns}
        dataSource={dataSource}
        pagination={{
          showSizeChanger: true,
        }}
      />
      <ApprovalModal
        visible={visible}
        initValues={record}
        onSuccess={() => {
          setVisible(false);
          load();
        }}
        onCancel={() => setVisible(false)}
      />
    </div>
  );
};
